/**
 * Adding subjects — one at a time, or a spreadsheet's worth (parity:
 * curator's bulk upload). Import is additive: a name that already exists is
 * reported as skipped, never overwritten.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Banner, Button, Input, Modal } from '@servicecompressionllc/sc-design-system';
import { knowledgeApi, type KnowledgeTemplate } from './api.js';

/** Minimal RFC-4180-ish reader: quoted fields, doubled quotes, CR/LF rows. */
export const parseDelimited = (text: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else quoted = false;
      } else field += ch;
      continue;
    }
    if (ch === '"') quoted = true;
    else if (ch === ',' || ch === '\t') {
      row.push(field);
      field = '';
    } else if (ch === '\n' || ch === '\r') {
      if (field.length > 0 || row.length > 0) {
        row.push(field);
        rows.push(row);
        row = [];
        field = '';
      }
      if (ch === '\r' && text[i + 1] === '\n') i += 1;
    } else field += ch;
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
};

/** Reads a name/type sheet into import rows. Header is required — it's how a
 *  user's column order stops mattering. */
export const rowsFromSheet = (text: string): { name: string; kind?: string | null }[] => {
  const grid = parseDelimited(text);
  if (grid.length === 0) return [];
  const header = grid[0]!.map((h) => h.trim().toLowerCase());
  const nameAt = header.indexOf('name');
  const kindAt = header.findIndex((h) => h === 'type' || h === 'kind');
  if (nameAt === -1) throw new Error('The first row must have a column called "name".');
  return grid
    .slice(1)
    .map((r) => ({
      name: (r[nameAt] ?? '').trim(),
      kind: kindAt === -1 ? null : (r[kindAt] ?? '').trim() || null,
    }))
    .filter((r) => r.name.length > 0);
};

const NEW_TYPE = '__new__';

export const NewSubjectModal = ({
  template,
  knownKinds,
  onClose,
}: {
  template: KnowledgeTemplate;
  /** Types already in use in this category. Curator's rule, ported: a type is
   *  REQUIRED once a category has them, and absent for flat categories. */
  knownKinds: string[];
  onClose: () => void;
}) => {
  const qc = useQueryClient();
  const templateId = template.id;
  const noun = template.subjectNoun.toLowerCase();
  const [mode, setMode] = useState<'one' | 'sheet'>('one');
  const [name, setName] = useState('');
  const typed = knownKinds.length > 0;
  const [kind, setKind] = useState(typed ? (knownKinds[0] ?? '') : '');
  const [newKind, setNewKind] = useState('');
  const effectiveKind = kind === NEW_TYPE ? newKind.trim() : kind.trim();
  const [sheetError, setSheetError] = useState<string | null>(null);
  const [pending, setPending] = useState<{ name: string; kind?: string | null }[]>([]);
  const [fileName, setFileName] = useState('');

  const done = () => {
    void qc.invalidateQueries({ queryKey: ['knowledge'] });
    onClose();
  };

  const createOne = useMutation({
    mutationFn: () =>
      knowledgeApi.createSubject({
        templateId,
        name: name.trim(),
        kind: effectiveKind.length > 0 ? effectiveKind : null,
      }),
    onSuccess: done,
  });
  const importSheet = useMutation({
    mutationFn: () => knowledgeApi.importSubjects({ templateId, rows: pending }),
  });

  // FileReader, not file.text(): jsdom implements the former, so the code the
  // browser runs is the code the tests run.
  const onFile = (file: File | undefined) => {
    if (!file) return;
    setFileName(file.name);
    setSheetError(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const rows = rowsFromSheet(String(reader.result ?? ''));
        if (rows.length === 0) throw new Error('No rows with a name were found in that file.');
        setPending(rows);
      } catch (err) {
        setPending([]);
        setSheetError((err as Error).message);
      }
    };
    reader.readAsText(file);
  };

  const result = importSheet.data;

  return (
    <Modal open onOpenChange={(open) => !open && onClose()}>
      <Modal.Header>
        <Modal.Title>Add to {template.name}</Modal.Title>
        <Modal.CloseButton />
      </Modal.Header>
      <Modal.Body>
        <div className="mb-4 flex gap-1 border-b border-outline" role="tablist">
          {(
            [
              { id: 'one', label: `One ${noun}` },
              { id: 'sheet', label: 'From a spreadsheet' },
            ] as const
          ).map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={mode === t.id}
              onClick={() => setMode(t.id)}
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                mode === t.id
                  ? 'border-primary text-ink'
                  : 'border-transparent text-ink-muted hover:text-ink'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {mode === 'one' ? (
          <>
            <label className="mb-3 block text-sm font-medium text-ink">
              Name
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={`The name your team uses for one ${noun}`}
                aria-label="Subject name"
                className="mt-1 w-full"
              />
            </label>
            <div>
              <label htmlFor="subject-type" className="mb-1 block text-sm font-medium text-ink">
                Type {!typed && <span className="font-normal text-ink-subtle">(optional)</span>}
              </label>
              {typed ? (
                <>
                  {/* Chips, not a dropdown: an overlay panel gets clipped by
                      the modal's own scroll container. */}
                  <div className="flex flex-wrap gap-2">
                    {knownKinds.map((k) => (
                      <button
                        key={k}
                        type="button"
                        aria-pressed={kind === k}
                        onClick={() => setKind(k)}
                        className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                          kind === k
                            ? 'border-primary bg-surface-hover text-ink'
                            : 'border-outline text-ink-muted hover:text-ink'
                        }`}
                      >
                        {k}
                      </button>
                    ))}
                    <button
                      type="button"
                      aria-pressed={kind === NEW_TYPE}
                      onClick={() => setKind(NEW_TYPE)}
                      className={`rounded-full border border-dashed px-3 py-1 text-sm transition-colors ${
                        kind === NEW_TYPE
                          ? 'border-primary bg-surface-hover text-ink'
                          : 'border-outline text-ink-muted hover:text-ink'
                      }`}
                    >
                      + new type
                    </button>
                  </div>
                  {kind === NEW_TYPE && (
                    <Input
                      value={newKind}
                      onChange={(e) => setNewKind(e.target.value)}
                      placeholder="Name the new type"
                      aria-label="New type name"
                      className="mt-2 w-full"
                    />
                  )}
                </>
              ) : (
                <Input
                  id="subject-type"
                  value={kind}
                  onChange={(e) => setKind(e.target.value)}
                  placeholder="A sub-group these fall into, if you use them"
                  aria-label="Subject type"
                  className="w-full"
                />
              )}
            </div>
            {createOne.error && (
              <p className="mt-3 text-sm text-danger">{(createOne.error as Error).message}</p>
            )}
          </>
        ) : (
          <>
            <p className="text-sm text-ink-muted">
              A CSV whose first row names the columns. <code>name</code> is required;{' '}
              <code>type</code> is optional and tags each {noun}.
            </p>
            <input
              type="file"
              accept=".csv,.tsv,.txt"
              aria-label="Spreadsheet file"
              onChange={(e) => onFile(e.target.files?.[0])}
              className="mt-3 block text-sm text-ink"
            />
            {sheetError && (
              <Banner variant="danger" className="mt-3">
                <Banner.Description>{sheetError}</Banner.Description>
              </Banner>
            )}
            {pending.length > 0 && !result && (
              <p className="mt-3 text-sm text-ink">
                <strong>{pending.length}</strong> rows found in {fileName}. They’ll be added to{' '}
                {template.name}; anything already there is left alone.
              </p>
            )}
            {result && (
              <Banner variant={result.errors.length > 0 ? 'warning' : 'success'} className="mt-3">
                <Banner.Title>
                  Added {result.created.length}
                  {result.skipped.length > 0
                    ? `, skipped ${String(result.skipped.length)} already present`
                    : ''}
                </Banner.Title>
                {result.errors.length > 0 && (
                  <Banner.Description>
                    {result.errors.length} couldn’t be added:{' '}
                    {result.errors.map((e) => e.name).join(', ')}
                  </Banner.Description>
                )}
              </Banner>
            )}
            {importSheet.error && (
              <p className="mt-3 text-sm text-danger">{(importSheet.error as Error).message}</p>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          {result ? 'Close' : 'Cancel'}
        </Button>
        {mode === 'one' ? (
          <Button
            onClick={() => createOne.mutate()}
            disabled={
              createOne.isPending ||
              name.trim().length === 0 ||
              (typed && effectiveKind.length === 0)
            }
          >
            {createOne.isPending ? 'Adding…' : `Add ${noun}`}
          </Button>
        ) : (
          !result && (
            <Button
              onClick={() => importSheet.mutate()}
              disabled={importSheet.isPending || pending.length === 0}
            >
              {importSheet.isPending ? 'Importing…' : `Import ${String(pending.length)}`}
            </Button>
          )
        )}
      </Modal.Footer>
    </Modal>
  );
};
